import { useState, useEffect, useCallback } from 'react';
import proxy from '@/services/ProxyService';
import { useInterfaceStore } from '@/store/interface';
import { Search } from '@mui/icons-material';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
} from '@mui/material';
import {
  POSSIBLE_SOURCES,
  transformToInternalSchema,
} from './partyManagerUtils';
import { Add } from '@mui/icons-material';

const PartyQuickEntry = ({ open, onClose, onSelect }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const INTERFACE_STORE = useInterfaceStore.getState();

  const performSearch = useCallback(
    async (query) => {
      if (!query.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        // Tanss
        const { data } = await proxy.put(`${INTERFACE_STORE.tanss}/search`, {
          areas: ['COMPANY', 'EMPLOYEE'],
          query: query,
        });
        const { companies = [], employees = [] } = data.content;
        const formattedCompanies = companies.map((item) => ({
          ...item,
          displayName: `${item?.displayId ? `[${item?.displayId}]` : ''} ${item.name}`,
          source: 'tanss',
          displayType: 'Firma',
          partyType: 'ORGANIZATION',
        }));
        const formattedEmployees = employees.map((item) => ({
          ...item,
          displayName: `${item?.displayId ? `[${item?.displayId}]` : ''} ${item.name}`,
          source: 'tanss',
          displayType: 'Mitarbeiter',
          partyType: 'PERSON',
        }));
        // Weclapp
        const {
          data: { result: parties },
        } = await proxy.get(
          `${INTERFACE_STORE.weclapp}/party?or-customerNumber-ilike=${query}&or-supplierNumber-ilike=${query}&or-company-ilike=${query}&or-company2-ilike=${query}&or-firstName-ilike=${query}&or-lastName-ilike=${query}&or-email-ilike=${query}`,
        );
        const formattedParties = parties.map((item) => ({
          ...item,
          displayName:
            `${item?.customerNumber || item.supplierNumber ? `[${item?.customerNumber ?? item.supplierNumber}]` : ''} ${item?.company ?? ''} ${item?.company2 ?? ''} ${item?.firstName ?? ''} ${item?.lastName ?? ''}`
              .trim()
              .replace(/\s+/g, ' '),
          source: 'weclapp',
          displayType: item.partyType === 'ORGANIZATION' ? 'Firma' : 'Person',
        }));
        console.log(parties);

        setResults([
          ...formattedParties,
          ...formattedCompanies,
          ...formattedEmployees,
        ]);
      } catch (error) {
        console.error('Suche fehlgeschlagen:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [INTERFACE_STORE.tanss],
  );

  useEffect(() => {
    if (!searchTerm) {
      setResults([]);
      return;
    }
    const handler = setTimeout(() => {
      performSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm, performSearch]);

  const handleManualSearch = () => {
    performSearch(searchTerm);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: '500px', maxWidth: 'none' } }}
    >
      <DialogTitle>Kontakt Schnellerfassung</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Suche nach Firmen oder Personen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mt: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleManualSearch();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleManualSearch}
                  edge="end"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : <Search />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <List sx={{ mt: 1 }}>
          {results.map((result, index) => {
            const sourceInfo = POSSIBLE_SOURCES[result.source];
            return (
              <ListItem
                key={result.id || index}
                divider
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => {
                      const transformedObj = transformToInternalSchema(
                        result.source,
                        result,
                      );
                      onSelect(transformedObj);
                    }}
                  >
                    <Add />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    src={sourceInfo?.icon}
                    alt={sourceInfo?.name}
                    variant="rounded"
                    sx={{ width: 24, height: 24, bgcolor: 'transparent' }}
                  >
                    {result.source?.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={result.displayName}
                  secondary={result.displayType}
                />
              </ListItem>
            );
          })}

          {!loading && searchTerm && results.length === 0 && (
            <ListItem>
              <ListItemText
                secondary="Keine Ergebnisse gefunden"
                sx={{ textAlign: 'center' }}
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default PartyQuickEntry;
